import { create } from 'zustand'
import { apiFetch } from '../services/api'
import { getEffectiveTier } from './tier'
import { track } from './track'
import { guardedAccent, accentVarMap } from './accent'
import { useUserStore, type UserProfile } from '../stores/userStore'
import { useFlagStore } from '../hooks/useFeatureFlags'

/**
 * Theme engine — applies / persists / describes the preset palettes defined
 * as `:root[data-theme="…"]` blocks in themes.css (Task 1). Midnight is the
 * base (no `data-theme` attribute), matching the prototype and index.css.
 *
 * ⚠️ DUPLICATION NOTICE: the pre-paint script in index.html hardcodes the
 * theme-id list and the id→chrome map below (it runs before any module can
 * load, so it can't import this file). If you add / rename a theme or change
 * a chrome hex here, update the inline script in apps/web/index.html to match.
 */

export type ThemeId =
  | 'midnight'
  | 'light'
  | 'dawn'
  | 'rose'
  | 'sage'
  | 'golden'
  | 'moonstone'

/** Hardcoded mini-preview colors for the appearance picker swatch card.
 *  These are DELIBERATE literals copied verbatim from the design handoff's
 *  `CF_THEMES[].sw` — they render a static thumbnail of each palette and must
 *  NOT be tokenized / re-themed (the whole point is that a Rose swatch looks
 *  like Rose while the page is on Sage). Kept in the token-guard allowlist via
 *  inline `theme-audit` tags on each entry. */
export interface ThemeSwatch {
  /** Page background of the mini-preview. */
  page: string
  /** Lifted card surface inside the mini-preview. */
  card: string
  /** Accent dot color. */
  accent: string
  /** Text-bar color inside the mini card. */
  text: string
}

export interface ThemeInfo {
  id: ThemeId
  /** User-facing name for the picker (Task 3 / batch 3). */
  label: string
  /** One-word mood used as picker sub-copy. */
  emotion: string
  /** The theme's `--color-cosmic-topbar` hex — mirrored into the
   *  `theme-color` meta so mobile browser chrome matches the surface. */
  chrome: string
  /** Hardcoded mini-preview swatch colors (see {@link ThemeSwatch}). */
  swatch: ThemeSwatch
}

/** The canonical theme registry. `midnight` is the base (data-theme removed);
 *  `light` becomes the default at the launch flip (batch 3). Chrome hexes are
 *  copied verbatim from each theme's `--color-cosmic-topbar` in themes.css
 *  (midnight lives in index.css). */
// labels + emotions are copied VERBATIM from the design handoff's CF_THEMES
// (design system 7-11-26 → cosmic-app/appearance.jsx); do not paraphrase.
// Each entry carries two kinds of hardcoded literals — both deliberate, both
// tagged `theme-audit` inline so the color guard allows them:
//   • chrome — mirrors that theme's --color-cosmic-topbar in themes.css.
//   • swatch — the appearance-picker mini-preview colors, copied verbatim from
//     the design handoff's CF_THEMES[].sw; these must NEVER re-theme.
// Registry order = the design handoff's CF_THEMES display order (Daylight leads,
// Midnight last, Golden hour before Sage). AppearancePage renders THEMES.map
// directly, so this array IS the picker grid order; keep it in lockstep with the
// shared THEME_IDS const (pinned order-exact by theme.shared.test.ts). Nothing
// depends on a particular index (DEFAULT_THEME is a named const, the pre-paint /
// contrast scripts key by id) so the order is purely presentational.
export const THEMES: readonly ThemeInfo[] = [
  {
    id: 'light', label: 'Daylight', emotion: 'Clear & open',
    chrome: '#ece1c4', // theme-audit: mirrors themes.css --color-cosmic-topbar
    swatch: { page: '#f3ead4', card: '#fff8e4', accent: '#4a6b88', text: '#2a2417' }, // theme-audit: picker mini-preview literals, never re-themed
  },
  {
    id: 'dawn', label: 'Dawn', emotion: 'Intuitive & soft',
    chrome: '#e4dded', // theme-audit: mirrors themes.css --color-cosmic-topbar
    swatch: { page: '#efeaf3', card: '#fbf8fd', accent: '#6557d1', text: '#221d33' }, // theme-audit: picker mini-preview literals, never re-themed
  },
  {
    id: 'rose', label: 'Rose quartz', emotion: 'Warm-hearted',
    chrome: '#f0dfda', // theme-audit: mirrors themes.css --color-cosmic-topbar
    swatch: { page: '#f6ebe8', card: '#fff7f4', accent: '#b13d55', text: '#33201d' }, // theme-audit: picker mini-preview literals, never re-themed
  },
  {
    id: 'golden', label: 'Golden hour', emotion: 'Optimistic',
    chrome: '#efe0c2', // theme-audit: mirrors themes.css --color-cosmic-topbar
    swatch: { page: '#f7ecd9', card: '#fff8e8', accent: '#96600e', text: '#2e2413' }, // theme-audit: picker mini-preview literals, never re-themed
  },
  {
    id: 'sage', label: 'Sage', emotion: 'Grounded',
    chrome: '#e3e7d6', // theme-audit: mirrors themes.css --color-cosmic-topbar
    swatch: { page: '#eef0e6', card: '#fbfcf4', accent: '#3f6b4f', text: '#232a1f' }, // theme-audit: picker mini-preview literals, never re-themed
  },
  {
    id: 'moonstone', label: 'Moonstone', emotion: 'Calm & clear',
    chrome: '#dde3ea', // theme-audit: mirrors themes.css --color-cosmic-topbar
    swatch: { page: '#e9edf2', card: '#fafcff', accent: '#3f648a', text: '#1f2733' }, // theme-audit: picker mini-preview literals, never re-themed
  },
  {
    id: 'midnight', label: 'Midnight', emotion: 'Focused ritual',
    chrome: '#111118', // theme-audit: mirrors themes.css --color-cosmic-topbar
    swatch: { page: '#0d0d12', card: '#1a1a26', accent: '#7a9ab5', text: '#e8e2d9' }, // theme-audit: picker mini-preview literals, never re-themed
  },
] as const

/**
 * The base / default theme — rendered by `:root` with no `data-theme`
 * attribute, and the effective theme for anyone without a saved paid choice.
 *
 * ⚠️ LAUNCH FLIP: this is `'midnight'` today (dark base, matching the current
 * production look). At the batch-3 launch flip this becomes `'light'`
 * (Daylight) so unauthenticated / free surfaces default to the light palette.
 * Change this ONE constant (and the pre-paint default in index.html) to flip.
 */
export const DEFAULT_THEME: ThemeId = 'midnight'

/** localStorage key mirroring the server-truth theme for PAID users. */
export const THEME_STORAGE_KEY = 'cf:theme'

/** sessionStorage key holding a free/paid user's in-session preview theme.
 *  Preview is session-scoped and NEVER written to the server or `cf:theme`. */
export const THEME_PREVIEW_KEY = 'cf:theme-preview'

/** localStorage key mirroring the server-truth custom accent hue (PAID users).
 *  Read by the pre-paint script to restore the cached accent vars before first
 *  paint. `null`/absent means no custom accent (default gold/slate accents). */
export const ACCENT_HUE_STORAGE_KEY = 'cf:accent-hue'

/** localStorage key holding the pre-computed accent vars for the CURRENT theme:
 *  `{ theme, hue, vars }`. The pre-paint script applies `vars` inline ONLY when
 *  it still matches the theme being restored AND the mirrored hue — it never
 *  runs the guard math itself (it can't import accent.ts). Recomputed by
 *  {@link applyAccent} whenever the effective theme or the hue changes. */
export const ACCENT_VARS_CACHE_KEY = 'cf:accent-vars'

/** The nine accent CSS custom properties {@link accentVarMap} drives. Kept as a
 *  literal list so we can REMOVE them without recomputing guard math (suppress /
 *  clear). Must stay in lockstep with the keys accentVarMap returns —
 *  accent.test.ts / the store tests lock the shape. */
export const ACCENT_VAR_KEYS: readonly string[] = [
  '--color-gold',
  '--color-slate',
  '--color-accent-fg',
  '--color-gold-light',
  '--color-slate-light',
  '--color-accent-fg-hi',
  '--color-accent-bg',
  '--color-accent-border',
  '--color-accent-glow',
]

/**
 * Strip the nine accent custom properties from `<html>` — DOM ONLY, no
 * localStorage. Unlike `applyAccent(null, …)` (which also clears the
 * `cf:accent-hue` / `cf:accent-vars` mirrors), this touches nothing but the
 * inline style, so it is byte-safe to call under a disabled `theme_picker`
 * flag. Used by ThemeManager's flag-off branch to remove a stale pre-paint
 * accent (flag mirror `'1'` → server flag off) without any storage side effect.
 * `removeProperty` is a no-op when the property is unset.
 */
export function clearAccentVarsFromDom(): void {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  for (const key of ACCENT_VAR_KEYS) root.style.removeProperty(key)
}

/** Is `value` a valid custom accent hue — an integer degree in [0, 359]? */
export function isAccentHue(value: unknown): value is number {
  return (
    typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= 0 &&
    value <= 359
  )
}

/**
 * The theme a currently-mounted forced surface (useForcedTheme) is holding the
 * page on, or `null` when nothing is forcing. The boot / flag-load reconcile in
 * ThemeManager reads this so its correction NEVER clobbers an active forced
 * Daylight surface: without it, ThemeManager's `applyTheme(getStoredTheme())`
 * (a passive effect) runs after the forced surface's layout effect on the same
 * flag-load commit and would re-apply the user's stored theme on top of the
 * forced one. Layout effects flush before passive effects within a commit, so
 * by the time ThemeManager reads this it's already set. Module-level (not store)
 * state on purpose — it's read synchronously inside an effect, never rendered.
 */
let activeForcedTheme: ThemeId | null = null

/** Record (or clear) the theme a forced surface is holding. See {@link
 *  activeForcedTheme}. Pass `null` on unmount / when forcing stops.
 *
 * Also mirrors the value into the store's reactive `forcedTheme` field. The
 * module var above is the synchronous source of truth (read inside effects,
 * where layout-before-passive ordering guarantees it is already set); the store
 * mirror exists ONLY so ThemeManager RE-RUNS its reconcile when a forced surface
 * mounts / unmounts. Without it, navigating a signed-in paid user into a forced
 * surface (e.g. the admin portal forcing midnight) would never re-run
 * ThemeManager, so the custom accent would linger un-suppressed — and never be
 * restored on the way back. Guarded so an unchanged write skips the setState. */
export function setActiveForcedTheme(id: ThemeId | null): void {
  activeForcedTheme = id
  useThemeStore.setState((s) => (s.forcedTheme === id ? s : { forcedTheme: id }))
}

/** The theme a mounted forced surface is holding, or `null`. */
export function getActiveForcedTheme(): ThemeId | null {
  return activeForcedTheme
}

/**
 * Monotonic request-generation counter for `setTheme`. Each call increments it
 * and captures its own generation; only the call whose generation is still the
 * latest may apply its success mirror-write or its rollback. This sequences
 * overlapping picks: a rapid double-pick where the EARLIER PATCH rejects (or
 * responses arrive out of order) can no longer let a stale rollback clobber the
 * newer pick. Module-level (not store) state — read synchronously inside the
 * async setTheme closure, never rendered.
 */
let themeRequestGeneration = 0

const CHROME_BY_ID: Record<ThemeId, string> = THEMES.reduce(
  (acc, t) => {
    acc[t.id] = t.chrome
    return acc
  },
  {} as Record<ThemeId, string>,
)

const VALID_IDS = new Set<string>(THEMES.map((t) => t.id))

/** Type guard: is `value` one of the registered theme ids? */
export function isThemeId(value: unknown): value is ThemeId {
  return typeof value === 'string' && VALID_IDS.has(value)
}

/** Read the persisted theme id, validated. Invalid / missing → midnight.
 *  Never throws (private-mode / disabled storage falls through to default). */
export function getStoredTheme(): ThemeId {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY)
    if (isThemeId(stored)) return stored
  } catch {
    /* storage unavailable — use default */
  }
  return DEFAULT_THEME
}

/** Read the in-session preview theme id, validated. Missing / invalid → null.
 *  Never throws (private-mode / disabled storage falls through to null). */
export function getStoredPreview(): ThemeId | null {
  try {
    const stored = window.sessionStorage.getItem(THEME_PREVIEW_KEY)
    if (isThemeId(stored)) return stored
  } catch {
    /* storage unavailable — no preview */
  }
  return null
}

/** Read the persisted custom accent hue, validated. Missing / invalid → null.
 *  Never throws (private-mode / disabled storage falls through to null). */
export function getStoredAccentHue(): number | null {
  try {
    const raw = window.localStorage.getItem(ACCENT_HUE_STORAGE_KEY)
    if (raw === null) return null
    const n = Number(raw)
    if (isAccentHue(n)) return n
  } catch {
    /* storage unavailable — no accent */
  }
  return null
}

/** Persist (or clear) the in-session preview theme. Never throws. */
function writeStoredPreview(id: ThemeId | null): void {
  try {
    if (id === null) window.sessionStorage.removeItem(THEME_PREVIEW_KEY)
    else window.sessionStorage.setItem(THEME_PREVIEW_KEY, id)
  } catch {
    /* storage unavailable — preview lives in memory only */
  }
}

/** Is the given profile EFFECTIVELY paid right now? Honors the beta-tester
 *  `tierPreview` override, so a beta tester previewing 'free' counts as free —
 *  their stored paid theme is suppressed and the preview bar can show without
 *  any server write. Null profile (signed out / loading) → not paid. */
export function isPaidProfile(profile: UserProfile | null): boolean {
  return !!profile && getEffectiveTier(profile) === 'paid'
}

function currentIsPaid(): boolean {
  return isPaidProfile(useUserStore.getState().profile)
}

/** Is the `theme_picker` flag currently on? Read live from the flag store so
 *  store actions (setPreview) derive the effective theme with the same inputs
 *  ThemeManager uses — clearing a preview must respect flag-off / forced state. */
function themePickerEnabled(): boolean {
  return !!useFlagStore.getState().flags['theme_picker']
}

/** Update the `theme-color` meta so mobile browser chrome tracks the theme. */
function syncThemeColorMeta(chrome: string): void {
  if (typeof document === 'undefined') return
  let meta = document.querySelector(
    'meta[name="theme-color"]',
  ) as HTMLMetaElement | null
  if (!meta) {
    meta = document.createElement('meta')
    meta.setAttribute('name', 'theme-color')
    document.head.appendChild(meta)
  }
  meta.setAttribute('content', chrome)
}

let transitionCleanup: ReturnType<typeof setTimeout> | undefined

/** Has the boot correction already run? See {@link easeBackground}. */
let bootCorrectionDone = false

/** Test-only: put the module back in its pre-boot state, so each test can
 *  exercise the boot-correction branch from a known starting point. */
export function __resetBootCorrectionForTest(): void {
  bootCorrectionDone = false
}

/** The theme the DOM is currently on, read back from the attribute `applyTheme`
 *  writes. Bare `:root` (no attribute) IS the base theme, so an absent or
 *  unrecognized attribute reads as {@link DEFAULT_THEME}. */
function currentDomTheme(): ThemeId {
  const attr = document.documentElement.getAttribute('data-theme')
  return isThemeId(attr) ? attr : DEFAULT_THEME
}

/** Briefly ease the page background when switching themes, unless the user
 *  prefers reduced motion. The transition is added inline and cleared after
 *  it completes so it never interferes with other background changes.
 *
 *  `id` is the theme about to be applied; the DOM has not been updated yet. */
function easeBackground(id: ThemeId): void {
  if (typeof document === 'undefined' || !document.body) return

  // The FIRST applyTheme of a page load is the BOOT CORRECTION: React
  // reconciling whatever the index.html pre-paint script managed to resolve
  // against the real answer (flags, profile, a forced surface). That correction
  // must be instant. Easing it turns a residual mismatch into a ~200ms
  // background wipe, which reads as a bug rather than as a transition — it is
  // the visible half of the theme flash, and far more noticeable than the
  // one-frame swap it replaces. Every LATER call still eases: a deliberate pick
  // in the appearance picker, or an SPA nav onto a forced surface, are gestures
  // the user initiated and should feel animated.
  if (!bootCorrectionDone) {
    bootCorrectionDone = true
    return
  }

  // Nothing to ease — the DOM is already on this theme, so no background change
  // is coming. Skipping is not merely an optimization: during boot it is what
  // keeps the correction instant. The boot correction arrives as TWO applyTheme
  // calls in the SAME React commit — a forced surface's layout effect, then
  // ThemeManager's passive effect — and neither has painted yet. Without this
  // guard the second, idempotent call attaches a transition, and the browser
  // then animates the change the first call made. Measured on the production
  // build: `/` ramped rose→Daylight over ~180ms with `data-theme` already
  // correct, purely because of that second call.
  if (currentDomTheme() === id) return

  const prefersReduced =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  if (prefersReduced) return

  const body = document.body
  body.style.transition = 'background-color 200ms ease'
  if (transitionCleanup) clearTimeout(transitionCleanup)
  transitionCleanup = setTimeout(() => {
    body.style.transition = ''
  }, 250)
}

/**
 * Apply a theme: toggle the `data-theme` attribute (removed for the midnight
 * base) and sync the browser-chrome meta. Idempotent and safe to call on every
 * reconcile.
 *
 * `persist` (default true) writes the id to `cf:theme`. Pass `false` for the
 * boot-time reconcile so forcing midnight under a disabled flag does NOT
 * clobber the user's saved choice — that choice must survive for when the flag
 * flips back on. Only a deliberate user pick (`setTheme`) should persist.
 */
export function applyTheme(id: ThemeId, persist = true): void {
  const root = document.documentElement

  // Must run BEFORE the attribute below — it compares the incoming theme
  // against the one still on the DOM to decide whether anything will change.
  easeBackground(id)

  if (id === DEFAULT_THEME) {
    root.removeAttribute('data-theme')
  } else {
    root.setAttribute('data-theme', id)
  }

  syncThemeColorMeta(CHROME_BY_ID[id])

  if (persist) {
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, id)
    } catch {
      /* storage unavailable — DOM is still themed, just not persisted */
    }
  }

  // Notify subscribers that the effective theme — and thus the resolved values
  // of every design token — may have changed. Consumers that read computed
  // token values into non-CSS APIs (e.g. useClerkAppearance → Clerk's hex-only
  // `appearance`) subscribe to `themeVersion` to re-derive. Every applyTheme
  // path bumps it: setTheme (user pick), the boot reconcile, and forced themes
  // (useForcedTheme). Safe to call before the store is created — this function
  // only ever runs at runtime, long after module evaluation.
  bumpThemeVersion()
}

/** Increment the store's theme-change counter. Kept separate so `applyTheme`
 *  (declared before the store) can reference the store lazily at call time. */
function bumpThemeVersion(): void {
  useThemeStore.setState((s) => ({ themeVersion: s.themeVersion + 1 }))
}

/**
 * Apply (or remove) the custom accent. Given a hue and the CURRENT effective
 * theme id, this runs the contrast guard, sets the nine accent CSS custom
 * properties inline on `<html>`, and caches them so the pre-paint script can
 * restore them before first paint. Passing `hue === null` removes the nine vars
 * and clears both the `cf:accent-hue` mirror and the `cf:accent-vars` cache
 * (back to the theme's default gold/slate accents).
 *
 * ⚠️ This writes localStorage — callers MUST NOT invoke it while the
 * `theme_picker` flag is OFF (batch-2 byte-identical rule). ThemeManager gates
 * every call on flag-on; `setAccentHue` is paid-only (which implies the flag).
 */
export function applyAccent(hue: number | null, themeId: ThemeId): void {
  if (typeof document === 'undefined') return
  const root = document.documentElement

  if (hue === null || !isAccentHue(hue)) {
    for (const key of ACCENT_VAR_KEYS) root.style.removeProperty(key)
    try {
      window.localStorage.removeItem(ACCENT_HUE_STORAGE_KEY)
      window.localStorage.removeItem(ACCENT_VARS_CACHE_KEY)
    } catch {
      /* storage unavailable — DOM vars are already cleared */
    }
    return
  }

  const vars = accentVarMap(guardedAccent(hue, themeId))
  for (const [key, value] of Object.entries(vars)) {
    root.style.setProperty(key, value)
  }
  try {
    window.localStorage.setItem(ACCENT_HUE_STORAGE_KEY, String(hue))
    window.localStorage.setItem(
      ACCENT_VARS_CACHE_KEY,
      JSON.stringify({ theme: themeId, hue, vars }),
    )
  } catch {
    /* storage unavailable — DOM is accented, just not cached for pre-paint */
  }
}

/**
 * The single rule for WHETHER the custom accent should be on the page:
 *
 *   flag on AND effectively paid AND no forced surface AND a stored hue exists
 *
 * Computed against the CURRENT effective theme by the caller. A forced surface
 * (unauth Daylight) suppresses the accent just like it overrides the theme; a
 * free / tier-preview-free viewer never keeps a custom accent (accent is
 * paid-only); flag-off collapses to no accent (and the caller must not even
 * call applyAccent — see its localStorage warning).
 */
export function shouldApplyAccent(input: {
  flagEnabled: boolean
  isPaid: boolean
  forced: ThemeId | null
  storedAccentHue: number | null
}): boolean {
  return (
    input.flagEnabled &&
    input.isPaid &&
    input.forced === null &&
    input.storedAccentHue !== null
  )
}

/** Inputs to {@link deriveEffectiveTheme}. */
export interface EffectiveThemeInput {
  /** Is the `theme_picker` flag on? */
  flagEnabled: boolean
  /** A forced-surface theme (useForcedTheme), or null. */
  forced: ThemeId | null
  /** Is the viewer effectively paid (honors tierPreview)? */
  isPaid: boolean
  /** The server-truth stored theme (paid users). */
  stored: ThemeId
  /** The in-session preview theme, or null. */
  preview: ThemeId | null
}

/**
 * The single source of truth for WHICH theme should be on the page.
 *
 *   effective = forced ?? (flag off → DEFAULT)
 *                        : (preview ?? (isPaid ? stored : DEFAULT))
 *
 * - A forced surface always wins (unauth Daylight), matching batch 2.
 * - Flag OFF collapses to DEFAULT_THEME (midnight today) EXACTLY as batch 2 did
 *   — preview / stored / isPaid are ignored, so flag-off behavior is unchanged.
 * - A preview overrides the stored theme (free previews a paid palette).
 * - Free users NEVER see a stored (`cf:theme`) palette — they resolve to
 *   DEFAULT — so a stale mirror from a past paid session can't leak through.
 */
export function deriveEffectiveTheme(input: EffectiveThemeInput): ThemeId {
  if (input.forced) return input.forced
  if (!input.flagEnabled) return DEFAULT_THEME
  return input.preview ?? (input.isPaid ? input.stored : DEFAULT_THEME)
}

/** The theme the preview/upgrade bar refers to: an active preview if present,
 *  else the stored (paid) palette that a now-free viewer would lose. */
export function previewedTheme(input: {
  preview: ThemeId | null
  stored: ThemeId
}): ThemeId {
  return input.preview ?? input.stored
}

/**
 * Should Task 4's preview / upgrade bar show? True when the viewer is NOT
 * entitled to keep the palette their intent currently reflects:
 *
 *   !isPaid && previewedTheme !== DEFAULT_THEME
 *
 * Covers both a free user previewing a paid palette (`preview` set) AND a beta
 * tester who flips `tierPreview` to free while a paid `stored` theme lingers —
 * the bar appears with NO server write, because it's a pure client derivation.
 */
export function shouldShowPreviewBar(input: {
  isPaid: boolean
  preview: ThemeId | null
  stored: ThemeId
  /** The theme a mounted forced surface is holding, or null. See below. */
  forced?: ThemeId | null
}): boolean {
  if (input.isPaid) return false
  // A forced surface (marketing / auth / pillar pages force Daylight) overrides
  // the palette actually on screen, so `previewedTheme` is NOT what the viewer
  // is looking at — and this bar names it out loud. Without this guard a
  // signed-out visitor whose localStorage still holds `cf:theme=rose` is told
  // "Previewing Rose quartz — keep it with Cosmic" on a page rendering
  // Daylight. `deriveEffectiveTheme` already gives `forced` precedence; this
  // predicate has to agree with it or the bar and the page disagree.
  if (input.forced) return false
  return previewedTheme(input) !== DEFAULT_THEME
}

interface ThemeStore {
  /** The last effective theme applied to the DOM (kept in sync by ThemeManager;
   *  useForcedTheme restores to this on unmount). */
  theme: ThemeId
  /** Server-truth stored theme for paid users, mirrored in `cf:theme`. */
  stored: ThemeId
  /** Server-truth custom accent hue for paid users (0–359), mirrored in
   *  `cf:accent-hue`; null = no custom accent (theme default accents). */
  storedAccentHue: number | null
  /** In-session preview theme (sessionStorage `cf:theme-preview`), or null. */
  preview: ThemeId | null
  /** Reactive mirror of the module-level {@link activeForcedTheme}. Not read for
   *  its value (effects read {@link getActiveForcedTheme} synchronously); it is a
   *  ThemeManager effect dependency so the reconcile re-runs when a forced
   *  surface mounts / unmounts. See {@link setActiveForcedTheme}. */
  forcedTheme: ThemeId | null
  /** Monotonic counter bumped on every `applyTheme`. A cheap re-derive trigger
   *  for consumers that snapshot computed token values (see `applyTheme`). */
  themeVersion: number
  /** Last `setTheme` persistence error, for Task 4 to surface (null = none). */
  error: string | null
  /**
   * Persist a deliberate theme pick. PAID only: applies + writes `cf:theme` +
   * PATCHes the server, rolling back (DOM + store) and setting `error` on
   * failure. If the viewer is NOT effectively paid this delegates to
   * `setPreview` so a stray call can NEVER write the server or `cf:theme`.
   */
  setTheme: (id: ThemeId) => Promise<void>
  /**
   * Persist a deliberate custom-accent pick (or clear with `null`). PAID only:
   * a non-paid caller is a NO-OP (the UI never offers accent to free users), so
   * this can never write the server or the accent mirror for a free viewer.
   * Optimistically applies the accent against the current effective theme,
   * PATCHes `/users/me {accentHue}`, and rolls back + sets `error` on failure.
   * Shares the `themeRequestGeneration` token with `setTheme`, so a newer theme
   * OR accent pick invalidates this call's success/rollback. Fires `accent_set`
   * only on a successful non-null pick.
   */
  setAccentHue: (hue: number | null) => Promise<void>
  /**
   * Set (or clear with `null`) the in-session preview. Applies WITHOUT
   * persisting `cf:theme` and never touches the server. Applying a preview
   * fires `theme_previewed`.
   */
  setPreview: (id: ThemeId | null) => void
  /** Reconcile store + `cf:theme` mirror from a freshly-loaded profile: the
   *  server theme wins. DOM re-application is left to ThemeManager so the
   *  flag-off / forced-surface rules still hold. */
  syncFromProfile: (profile: UserProfile) => void
  /** Clear the last persistence error. */
  clearError: () => void
}

/** Zustand store for theme state: server-truth `stored`, session `preview`, and
 *  the derived effective `theme`. See {@link deriveEffectiveTheme}. */
export const useThemeStore = create<ThemeStore>((set, get) => ({
  theme: getStoredTheme(),
  stored: getStoredTheme(),
  storedAccentHue: getStoredAccentHue(),
  preview: getStoredPreview(),
  forcedTheme: null,
  themeVersion: 0,
  error: null,

  setTheme: async (id: ThemeId) => {
    // Guard: only effectively-paid users persist. Anyone else previews — this
    // guarantees a free user's action can never reach the server or cf:theme.
    if (!currentIsPaid()) {
      get().setPreview(id)
      return
    }

    // Claim this pick's generation. Only the latest generation may commit its
    // success/rollback below, so an earlier in-flight pick can never clobber a
    // newer one (out-of-order or late-rejecting responses).
    const generation = ++themeRequestGeneration
    const previous = get().stored
    // Optimistic: apply + mirror to cf:theme immediately, drop any preview.
    applyTheme(id, true)
    writeStoredPreview(null)
    set({ stored: id, preview: null, theme: id, error: null })

    try {
      const res = await apiFetch('/users/me', {
        method: 'PATCH',
        body: JSON.stringify({ theme: id }),
      })
      if (!res.ok) throw new Error(`theme save failed (${res.status})`)
      // A newer pick superseded this one while the request was in flight — it
      // now owns the store/DOM. Don't mirror this stale success onto the profile.
      if (generation !== themeRequestGeneration) return
      // Keep the user profile in sync so a later reconcile is a no-op.
      useUserStore.setState((s) => ({
        profile: s.profile ? { ...s.profile, theme: id } : s.profile,
      }))
      track('theme_applied', { theme: id })
    } catch {
      // A newer pick superseded this one — its optimistic state is the truth, so
      // this stale failure must NOT roll back to `previous` or surface an error.
      if (generation !== themeRequestGeneration) return
      // Roll back DOM + mirror + store to the prior theme and surface an error.
      applyTheme(previous, true)
      set({
        stored: previous,
        theme: previous,
        error: 'Could not save your theme. Please try again.',
      })
    }
  },

  setAccentHue: async (hue: number | null) => {
    // Guard: paid-only. A non-paid caller can NEVER reach the server or write
    // the accent mirror — the UI never surfaces the picker to free users.
    if (!currentIsPaid()) return

    // Share setTheme's generation token: a newer theme OR accent pick claims a
    // higher generation, so this call's late success/rollback becomes a no-op
    // and can't clobber the newer intent (out-of-order / late responses).
    const generation = ++themeRequestGeneration
    const previous = get().storedAccentHue
    // The theme the accent must be readable against = the current effective one.
    const effective = get().theme
    // Optimistic: apply (or clear) + mirror + cache immediately.
    applyAccent(hue, effective)
    set({ storedAccentHue: hue, error: null })

    try {
      const res = await apiFetch('/users/me', {
        method: 'PATCH',
        body: JSON.stringify({ accentHue: hue }),
      })
      if (!res.ok) throw new Error(`accent save failed (${res.status})`)
      // A newer pick superseded this one while in flight — it owns the DOM/store.
      if (generation !== themeRequestGeneration) return
      useUserStore.setState((s) => ({
        profile: s.profile ? { ...s.profile, accentHue: hue } : s.profile,
      }))
      // Only a committed (non-null) hue is an "accent_set"; clearing fires nothing.
      if (hue !== null) track('accent_set', { hue })
    } catch {
      // A newer pick superseded this one — its optimistic state is the truth, so
      // this stale failure must NOT roll back or surface an error.
      if (generation !== themeRequestGeneration) return
      // Roll back DOM (vars) + mirror + cache + store to the prior hue.
      applyAccent(previous, effective)
      set({
        storedAccentHue: previous,
        error: 'Could not save your accent color. Please try again.',
      })
    }
  },

  setPreview: (id: ThemeId | null) => {
    if (id === null) {
      writeStoredPreview(null)
      set({ preview: null })
      // Fall back to the effective non-preview theme (never persists cf:theme).
      // Re-derive through deriveEffectiveTheme with the live flag + forced inputs
      // so clearing a preview honors flag-off (→ DEFAULT) and can never override
      // a mounted forced surface (unauth Daylight) — same rules ThemeManager uses.
      applyTheme(
        deriveEffectiveTheme({
          flagEnabled: themePickerEnabled(),
          forced: getActiveForcedTheme(),
          isPaid: currentIsPaid(),
          stored: get().stored,
          preview: null,
        }),
        false,
      )
      return
    }
    writeStoredPreview(id)
    set({ preview: id })
    // persist:false — a preview must NEVER write the server-truth mirror.
    applyTheme(id, false)
    track('theme_previewed', {
      theme: id,
      tier: currentIsPaid() ? 'paid' : 'free',
    })
  },

  syncFromProfile: (profile: UserProfile) => {
    const paid = isPaidProfile(profile)
    // --- Theme ---
    // Server is source of truth for paid users; free users have no server theme
    // (enforced null) so they collapse to DEFAULT.
    const serverTheme =
      paid && isThemeId(profile.theme) ? profile.theme : DEFAULT_THEME
    if (get().stored !== serverTheme) {
      // Server wins: update store + re-mirror cf:theme. DOM application is left
      // to ThemeManager's derivation (so flag-off / forced surfaces still win).
      set({ stored: serverTheme })
      try {
        window.localStorage.setItem(THEME_STORAGE_KEY, serverTheme)
      } catch {
        /* storage unavailable — store state still reflects server truth */
      }
    }

    // --- Accent hue ---
    // Same server-wins rule: free users have accentHue enforced null server-side,
    // so a lingering mirror collapses. DOM application (guard math against the
    // effective theme) is left to ThemeManager. Handled independently of the
    // theme block above so a no-op theme never skips the accent reconcile.
    const serverHue = paid && isAccentHue(profile.accentHue) ? profile.accentHue : null
    if (get().storedAccentHue !== serverHue) {
      set({ storedAccentHue: serverHue })
      try {
        if (serverHue === null) {
          window.localStorage.removeItem(ACCENT_HUE_STORAGE_KEY)
        } else {
          window.localStorage.setItem(ACCENT_HUE_STORAGE_KEY, String(serverHue))
        }
      } catch {
        /* storage unavailable — store state still reflects server truth */
      }
    }
  },

  clearError: () => set({ error: null }),
}))
